import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * A React hook that tracks the state of a CSS media query.
 *
 * This hook listens for changes to the specified media query and returns
 * whether it currently matches. It handles SSR by returning false when
 * window is undefined.
 *
 * @param {string} query - A valid CSS media query string (e.g., '(max-width: 768px)')
 * @returns {boolean} Whether the media query currently matches
 *
 * @example
 * const isLargeScreen = useMediaQuery('(min-width: 1024px)')
 *
 * @example
 * const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')
 */
function useMediaQuery(query) {
  const mediaQueryRef = useRef(null)

  const getMatches = useCallback(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(query).matches
  }, [query])

  const [matches, setMatches] = useState(getMatches)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia(query)
    mediaQueryRef.current = mediaQuery

    const handleChange = () => setMatches(mediaQuery.matches)

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [query])

  return matches
}

export default useMediaQuery
